import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast, QueryLoader } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import {
  useUpdateCaseProfileOwnersCompletedMutation,
  useRemoveProfileFromCaseMutation,
  useGetProfilesForCaseQuery,
} from "../../graphql/supergraphHooks";
import { isJoinAccountType, resolvedAccountType } from "../../utils/account_types";
import { clientContext } from "../../utils/graphql_utils";
import { BeneficialOwnerSummaryList } from "../BeneficialOwnerSummaryList";
import { Owner } from "../BeneficialOwnerSummaryList/types";
import { DeleteProfileModal } from "../DeleteProfileModal";
import { ProfileToDelete } from "../DeleteProfileModal/types";
import { getAccountTypeLabel } from "../profile/utils";
import { provideLegalName } from "../ProfileTree/utils";

export const BeneficialOwnersScreen = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { rootProfileId, caseId, basePath, isReadOnly } = useComplianceCaseContext();
  const history = useHistory();

  const [ownerBeingDeleted, setOwnerBeingDeleted] = useState<ProfileToDelete | null>(null);

  const profileResults = useGetProfilesForCaseQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseId: Number(caseId),
    },
  });

  const [updateOwnersCompleted] = useUpdateCaseProfileOwnersCompletedMutation({
    onError: () => toast.error("Error marking owners as completed."),
    onCompleted: () => {
      toast.success("Owners marked as completed successfully");
    },
    refetchQueries: ["GetProfilesForCase", "GetComplianceCase"],
    awaitRefetchQueries: true,
  });

  const [removeProfileFromCase] = useRemoveProfileFromCaseMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Profile deleted");
      setOwnerBeingDeleted(null);
    },
    refetchQueries: ["GetProfilesForCase"],
    awaitRefetchQueries: true,
  });

  const isRoot = Number(profileId) === rootProfileId;
  const complianceCase = profileResults.data?.complianceCase;
  const allProfiles = complianceCase?.profiles.concat([complianceCase?.investingProfile]);
  const profile = allProfiles?.find((p) => p?.id === parseInt(profileId));

  // Get the entity name from the case result
  const entityName = profile?.legalName || "";

  // Transform the data into the format expected by ControllerIndividualSelection
  const owners: Owner[] =
    profile?.beneficialOwners.map((person) => {
      return {
        id: person?.childProfile?.id?.toString() || "",
        name: provideLegalName(person.childProfile),
        type: getAccountTypeLabel(resolvedAccountType(person.childProfile)),
        relationship: "Owner",
        ownershipPercentage: person.percentOwnership || 0,
      };
    }) || [];

  const handleCompleted = async () => {
    await updateOwnersCompleted({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
        ownersCompleted: true,
      },
    });
    closePage();
  };

  const handleDelete = (id: string) => {
    const ownerBeingDeletedData = id
      ? profile?.beneficialOwners.find((profile) => profile.childProfile.id.toString() === id)
      : null;

    if (!ownerBeingDeletedData?.childProfile?.id) {
      toast.error("Could not find profile to delete");
      return;
    }

    let isOrHasPaymentBeneficiary = false;
    let descendantNames: string[] = [];

    if (complianceCase?.profiles) {
      const profileAndDescendants = complianceCase?.profiles.filter((profile) =>
        profile?.path?.includes(`/${ownerBeingDeletedData.childProfile.id}/`),
      );
      isOrHasPaymentBeneficiary = profileAndDescendants?.some((profile) =>
        profile?.parentRelationships.some(
          (relationship) => relationship.relationshipType === "payment_beneficiary",
        ),
      );
      const re = new RegExp(`\\/${ownerBeingDeletedData.childProfile.id}\\/\\d`);
      descendantNames = profileAndDescendants
        ?.filter((profile) => re.test(profile?.path || ""))
        .map((profile) => provideLegalName(profile));
    }

    setOwnerBeingDeleted({
      id: ownerBeingDeletedData.childProfile.id.toString(),
      name: provideLegalName(ownerBeingDeletedData.childProfile),
      isOrHasPaymentBeneficiary: isOrHasPaymentBeneficiary,
      descendantNames: descendantNames,
    });
  };

  const handleConfirmDelete = async (caseId: number, profileId: string) => {
    await removeProfileFromCase({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
      },
    });
  };

  const handleCreateOwner = () => {
    history.push(`${basePath}/upsert-owners/${profileId}`);
  };

  const handleEditOwner = (id: string) => {
    history.push(`${basePath}/upsert-owners/${profileId}/${id}`);
  };

  const closePage = () => {
    history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
  };

  return (
    <QueryLoader result={profileResults}>
      <BeneficialOwnerSummaryList
        entityName={entityName}
        childOwnershipThreshold={profile?.childOwnershipThreshold}
        isJoinAccount={isJoinAccountType(resolvedAccountType(profile))}
        owners={owners}
        onAddOwner={handleCreateOwner}
        onEdit={handleEditOwner}
        onDelete={handleDelete}
        onCompleted={handleCompleted}
        onBack={closePage}
        readOnly={isReadOnly}
      />
      <DeleteProfileModal
        profile={ownerBeingDeleted}
        caseId={Number(caseId)}
        show={!!ownerBeingDeleted}
        onClose={() => setOwnerBeingDeleted(null)}
        onDelete={handleConfirmDelete}
      />
    </QueryLoader>
  );
};
